import { useEffect, useState } from "react";
import { CalendarCheck, ExternalLink, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { api, getErrorMessage } from "@/api";
import ProfileInsights from "@/components/ProfileInsights";

export default function PortfolioPage({ onUserChange, user }) {
  const [talent, setTalent] = useState([]);
  const [media, setMedia] = useState([]);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [portfolio, setPortfolio] = useState(user?.portfolio || {});

  const normalizePortfolio = (profile = {}) => ({
    ...profile,
    skills: Array.isArray(profile.skills) ? profile.skills.join(", ") : profile.skills || "",
    past_events: Array.isArray(profile.past_events)
      ? profile.past_events.join(", ")
      : profile.past_events || "",
  });

  const toList = (value) => {
    if (Array.isArray(value)) return value;
    return (value || "").split(",").map((item) => item.trim()).filter(Boolean);
  };

  const loadTalent = async () => {
    const { data } = await api.get("/freelancers");
    setTalent(data);
  };

  useEffect(() => {
    loadTalent();
  }, []);

  useEffect(() => {
    setPortfolio(normalizePortfolio(user?.portfolio));
  }, [user]);

  const loadMedia = async () => {
    if (user?.role !== "freelancer") return;
    const { data } = await api.get("/portfolio/media");
    setMedia(data);
  };

  useEffect(() => {
    loadMedia();
  }, [user?.role]);

  const mediaUrl = (source) => {
    if (!source) {
      return "https://images.unsplash.com/photo-1542992933-ce75d0187ec1?auto=format&fit=crop&w=1200&q=85";
    }
    if (source?.startsWith("http")) return source;
    return `${process.env.REACT_APP_BACKEND_URL}/api/portfolio/media/${source}`;
  };

  const replaceBrokenImage = (event) => {
    const fallback = mediaUrl("");
    if (event.currentTarget.src !== fallback) event.currentTarget.src = fallback;
  };

  const uploadMedia = async (event) => {
    const image = event.target.files?.[0];
    if (!image) return;
    const formData = new FormData();
    formData.append("file", image);
    setUploading(true);
    try {
      await api.post("/portfolio/media", formData);
      toast.success("Event image added.");
      event.target.value = "";
      loadMedia();
      loadTalent();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  const removeMedia = async (mediaId) => {
    const shouldRemove = window.confirm("Remove this event image from your portfolio?");
    if (!shouldRemove) return;
    try {
      await api.delete(`/portfolio/media/${mediaId}`);
      toast.success("Event image removed.");
      loadMedia();
      loadTalent();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const savePortfolio = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        ...portfolio,
        skills: toList(portfolio.skills),
        past_events: toList(portfolio.past_events),
      };
      await api.put("/portfolio/me", payload);
      setEditing(false);
      toast.success("Portfolio updated.");
      loadTalent();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const ownProfile = user?.role === "freelancer" ? user : null;

  return (
    <main className="portfolio-page" data-testid="portfolio-page">
      <header className="portfolio-heading">
        <p className="eyebrow">THE CREW DIRECTORY</p>
        <h1>People who make<br /><em>the atmosphere.</em></h1>
        <p>Browse distinctive creative talent, scored by the teams who hired them.</p>
      </header>
      {ownProfile && <section className="own-portfolio" data-testid="own-portfolio-panel">
        <div><p className="eyebrow">YOUR PROFILE</p><strong data-testid="own-profile-name">{ownProfile.name}</strong></div>
        <button className="outline-button" data-testid="edit-portfolio-button" onClick={() => setEditing(!editing)}>
          {editing ? "Close editor" : "Edit portfolio"}
        </button>
        {editing && <form className="portfolio-editor" data-testid="portfolio-editor-form" onSubmit={savePortfolio}>
          {["headline", "bio", "skills", "availability", "starting_rate", "past_events"].map((key) => <label key={key}>
            {key.replace("_", " ")}
            <input data-testid={`portfolio-${key}-input`} onChange={(e) => setPortfolio({ ...portfolio, [key]: e.target.value })} value={portfolio[key] || ""} />
          </label>)}
          <button className="primary-button" data-testid="save-portfolio-button">Save profile</button>
        </form>}
        {editing && <div className="media-manager" data-testid="portfolio-media-manager">
          <label className="upload-label" data-testid="portfolio-upload-label">
            {uploading ? "Adding image…" : "Add event photo"}
            <input
              accept="image/jpeg,image/png,image/webp"
              data-testid="portfolio-upload-input"
              disabled={uploading}
              onChange={uploadMedia}
              type="file"
            />
          </label>
          <div className="media-strip" data-testid="portfolio-media-list">
            {media.map((item) => <figure data-testid={`portfolio-media-${item.id}`} key={item.id}>
              <img alt={item.original_filename} onError={replaceBrokenImage} src={mediaUrl(item.id)} />
              <button
                data-testid={`portfolio-media-remove-${item.id}`}
                onClick={() => removeMedia(item.id)}
                type="button"
              >
                Remove
              </button>
            </figure>)}
          </div>
        </div>}
        <ProfileInsights profile={ownProfile} profileSlug={ownProfile.profile_slug} onSlugChange={(profileSlug) => onUserChange({ profile_slug: profileSlug })} />
      </section>}
      <section className="talent-grid" data-testid="talent-grid">
        {talent.map((person, index) => <article className={`talent-card talent-card-${index + 1}`} data-testid={`talent-card-${person.id}`} key={person.id}>
          <img alt={`${person.name} event work`} data-testid={`talent-image-${person.id}`} onError={replaceBrokenImage} src={mediaUrl(person.portfolio?.gallery?.[0])} />
          {person.portfolio?.gallery?.length > 1 && <div className="talent-gallery" data-testid={`talent-gallery-${person.id}`}>
            {person.portfolio.gallery.slice(1, 4).map((image, imageIndex) => <img alt={`${person.name} event gallery ${imageIndex + 2}`} data-testid={`talent-gallery-image-${person.id}-${imageIndex}`} key={image} onError={replaceBrokenImage} src={mediaUrl(image)} />)}
          </div>}
          <div className="talent-info"><p className="eyebrow">{person.portfolio?.availability || "Available"}</p><h2 data-testid={`talent-name-${person.id}`}>{person.name}</h2><p data-testid={`talent-headline-${person.id}`}>{person.portfolio?.headline}</p><div className="talent-facts"><span><Star size={15} fill="currentColor" />{person.portfolio?.rating_average || "New"} ({person.portfolio?.rating_count || 0})</span><span><CalendarCheck size={15} />From ${person.portfolio?.starting_rate || 0}</span></div><Link className="talent-profile-link" data-testid={`talent-profile-link-${person.id}`} to={`/talent/${person.profile_slug}`}><ExternalLink size={14} /> View full portfolio</Link></div>
        </article>)}
      </section>
    </main>
  );
}